import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, ArrowRight, Star, Award, DollarSign, Clock, ChevronLeft, ChevronRight, Zap, TrendingUp, Target, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { testimonials, processSteps } from '../mock';
import LandingPageHeader from '../components/LandingPageHeader';
import LandingPageFooter from '../components/LandingPageFooter';
import ContactPopup from '../components/ContactPopup';
import ProcessSlider from '../components/ProcessSlider';
import PhoneInput from '../components/PhoneInput';
import { getGeoLocation, submitLead, getDialCodeFromCountryCode } from '../services/api';

// Hero Contact Form Component - Same fields as popup
const HeroContactForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [countryDialCode, setCountryDialCode] = useState('1');
  const [defaultCountryCode, setDefaultCountryCode] = useState('+1');

  // Fetch geolocation on mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const data = await getGeoLocation();
      if (data && data.isSuccess) {
        setGeoData(data);
        const dialCode = getDialCodeFromCountryCode(data.data.countryCode);
        setCountryDialCode(dialCode.toString());
        setDefaultCountryCode(`+${dialCode}`);
      }
    };
    fetchGeoLocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare lead data
      const leadData = {
        name: formData.name,
        email: formData.email,
        countryDialCode: parseInt(countryDialCode),
        country: geoData?.data?.countryCode || 'US',
        phoneNumber: formData.phone.replace(/\D/g, ''), // Only numeric
        ipAddress: geoData?.data?.ipAddress || '',
        description: formData.message,
        pageUrl: window.location.href,
        city: geoData?.data?.cityName || '',
        state: geoData?.data?.regionName || '',
        geoLocation: geoData ? JSON.stringify(geoData) : '',
        packageTitle: '',
        packageAmount: 0
      };

      const response = await submitLead(leadData);

      if (response.statusCode === 200 && response.data?.data?.customerId) {
        toast.success(response.data.data.message || 'Thank you! We\'ll contact you soon.');
        setFormData({ name: '', email: '', phone: '', message: '' });
        // Redirect to external application
        setTimeout(() => {
          window.location.href = `/application/main?customerid=${response.data.data.customerId}`;
        }, 1500);
      } else if (response.statusCode === 401) {
        toast.error(response.data?.message || 'Unauthorized access');
      } else if (response.statusCode === 400) {
        toast.error(response.data?.errorMessage || 'Invalid data provided');
      } else {
        toast.error(response.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border-2 border-white/20 shadow-2xl shadow-amber-500/20">
      <CardHeader className="pb-4 sm:pb-6">
        <CardTitle className="text-white text-xl sm:text-2xl font-bold">Get Your Free Consultation</CardTitle>
        <CardDescription className="text-gray-400 text-sm sm:text-base">
          Fill out the form and we'll get back to you within 24 hours.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div>
            <Label htmlFor="hero-name" className="text-gray-300">Name *</Label>
            <Input
              id="hero-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="bg-slate-800 border-white/20 text-white placeholder:text-gray-500 focus:border-white/40 mt-2"
              placeholder="Your name"
            />
          </div>

          <div>
            <Label htmlFor="hero-email" className="text-gray-300">Email *</Label>
            <Input
              id="hero-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="bg-slate-800 border-white/20 text-white placeholder:text-gray-500 focus:border-white/40 mt-2"
              placeholder="your@email.com"
            />
          </div>

          <PhoneInput
            id="hero-phone"
            value={formData.phone}
            onChange={handleChange}
            onCountryChange={(code) => setCountryDialCode(code)}
            defaultCountryCode={defaultCountryCode}
            required
            className="bg-slate-800 border-white/20 text-white placeholder:text-gray-500 focus:border-white/40"
          />

          <div>
            <Label htmlFor="hero-message" className="text-gray-300">Message *</Label>
            <Textarea
              id="hero-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={4}
              className="bg-slate-800 border-white/20 text-white placeholder:text-gray-500 focus:border-white/40 mt-2"
              placeholder="Tell us about your trademark needs..."
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-lg py-3 sm:py-4 md:py-6 text-xs sm:text-sm md:text-base lg:text-lg px-3 sm:px-4"
          >
            <span className="whitespace-normal leading-tight text-center block">
              {isSubmitting ? 'Sending...' : 'Register My Trademark Now'}
            </span>
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);

  // Update items per view based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setItemsPerView(3); // Desktop
      } else if (window.innerWidth >= 768) {
        setItemsPerView(2); // Tablet
      } else {
        setItemsPerView(1); // Mobile
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate max valid index based on items per view
  const maxTestimonialIndex = testimonials.length - itemsPerView;

  // Auto-slide testimonials every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => {
        return prev < maxTestimonialIndex ? prev + 1 : 0; // Loop back to start
      });
    }, 5000);

    return () => clearInterval(timer);
  }, [maxTestimonialIndex]);

  const packages = [
    {
      name: 'Deluxe',
      price: '$299',
      description: 'For brands needing extra support, including help with USPTO office actions.',
      note: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager'
      ],
      popular: true,
      recommended: true,
      disclaimer: '(Govt. filing fee not included)'
    },
    {
      name: 'Platinum',
      price: '$349',
      description: 'All-inclusive plan with monitoring, renewals, and full legal assistance.',
      note: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager',
        'High Approval Rate',
        'Comprehensive Trademark Search Report (Federal & State)',
        'High Priority Service',
        'Cease & Desist Letter'
      ],
      disclaimer: '(Govt. filing fee not included)'
    },
    {
      name: 'Abandoned Application Refiling',
      price: '$699',
      description: 'Complete refiling solution for abandoned trademark applications with 100% approval guarantee.',
      note: '+ Federal Filing Fee',
      features: [
        'Trademark Search & Risk Assessment',
        'Case Review & Filing by a U.S. Registered IP Attorney',
        'Federal Search & Clearance Report (All States)',
        'Unlimited office action responses',
        'FREE Attorney Consultation',
        'Monitoring Services for 5 yrs',
        'Application Drafting & Submission',
        'Filing an Objection Letter (if required)',
        'Copy of Cease & Desist Letter',
        '100% Approval Guarantee',
        'Private Registration (Optional)'
      ],
      disclaimer: '(Govt. filing fee not included)'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <LandingPageHeader />

      {/* Hero - Split Layout with Form */}
      <section className="relative py-12 sm:py-20 lg:py-32 overflow-hidden">
        {/* Dramatic background */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950" />
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-gradient-to-b from-amber-500/20 to-transparent rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start lg:items-center">
            {/* Left - Content */}
            <div className="order-2 lg:order-1">
              {/* Trust badge */}
              <div className="inline-flex items-center bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/40 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-6 sm:mb-8 animate-pulse">
                <CheckCircle className="h-4 sm:h-5 w-4 sm:w-5 text-green-400 mr-2" />
                <span className="text-green-400 font-bold text-xs sm:text-sm">98% Approval Rate • 5000+ Registered</span>
              </div>

              {/* Main headline */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white mb-6 sm:mb-8 leading-tight">
                Register Your Trademark in
                <span className="block mt-2 sm:mt-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600">
                  48 Hours or Less
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed font-medium">
                Protect your brand with expert trademark registration services. Fast, affordable, and backed by attorneys.
              </p>

              {/* Social proof */}
              <div className="flex flex-col gap-3 sm:gap-4 text-gray-400">
                <div className="flex items-center gap-2 sm:gap-3">
                  <CheckCircle className="h-5 sm:h-6 w-5 sm:w-6 text-amber-500 flex-shrink-0" />
                  <span className="font-semibold text-sm sm:text-base lg:text-lg">No Hidden Fees</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <CheckCircle className="h-5 sm:h-6 w-5 sm:w-6 text-amber-500 flex-shrink-0" />
                  <span className="font-semibold text-sm sm:text-base lg:text-lg">Money Back Guarantee</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <CheckCircle className="h-5 sm:h-6 w-5 sm:w-6 text-amber-500 flex-shrink-0" />
                  <span className="font-semibold text-sm sm:text-base lg:text-lg">24/7 Support</span>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="order-1 lg:order-2">
              <HeroContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section - Below Hero */}
      <section className="py-12 sm:py-16 relative bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              Trademark Registration Made Simple
            </h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Protect your business identity with our streamlined trademark registration process. Get started in minutes and secure your brand's future.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                step: "1",
                title: "Consultation & Search",
                desc: "Fill out our form and we'll conduct a comprehensive trademark search and assess your application's registrability"
              },
              {
                step: "2",
                title: "Application & Filing",
                desc: "Our attorneys prepare and file your complete application with the USPTO within 24-48 hours"
              },
              {
                step: "3",
                title: "Monitoring & Registration",
                desc: "We handle office actions and deliver your official trademark registration certificate upon approval"
              }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 rounded-2xl p-8 hover:border-white/40 hover:shadow-lg hover:shadow-white/10 transition-all h-full">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 w-14 h-14 rounded-full flex items-center justify-center text-white font-black text-2xl mb-6 shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
                {idx < 2 && (
                  <ArrowRight className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 text-amber-500 h-8 w-8 z-10" />
                )}
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-sm border border-white/20 rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                  Why Choose Professional Trademark Registration?
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  DIY trademark filing has a 60% rejection rate. With our expert attorneys, you get a 98% approval rate and complete peace of mind knowing your application is done right the first time.
                </p>
                <ul className="space-y-3">
                  {[
                    "Expert trademark search to avoid conflicts",
                    "Professional application preparation",
                    "USPTO office action handling included",
                    "Ongoing monitoring and protection"
                  ].map((point, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-4">
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-500/30 rounded-xl p-6">
                  <div className="text-4xl font-black text-amber-500 mb-2">98%</div>
                  <div className="text-white font-semibold mb-1">Success Rate</div>
                  <div className="text-gray-400 text-sm">Industry-leading approval rate with expert guidance</div>
                </div>
                <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/30 rounded-xl p-6">
                  <div className="text-4xl font-black text-green-400 mb-2">5000+</div>
                  <div className="text-white font-semibold mb-1">Brands Protected</div>
                  <div className="text-gray-400 text-sm">Trusted by businesses across all industries</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="text-4xl font-black text-blue-400 mb-2">24-48h</div>
                  <div className="text-white font-semibold mb-1">Fast Filing</div>
                  <div className="text-gray-400 text-sm">Quick turnaround without compromising quality</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Large icons & numbers */}
      <section className="py-12 sm:py-16 lg:py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-3 sm:mb-4">
              Why 5000+ Businesses Trust Us
            </h2>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {[
              {
                number: "01",
                icon: Target,
                title: "98% Success Rate",
                desc: "Industry-leading approval rate backed by experienced trademark attorneys",
                color: "from-blue-500 to-blue-600"
              },
              {
                number: "02",
                icon: Zap,
                title: "24-48 Hour Filing",
                desc: "Lightning-fast processing without compromising quality or accuracy",
                color: "from-amber-500 to-amber-600"
              },
              {
                number: "03",
                icon: Shield,
                title: "Lifetime Support",
                desc: "Ongoing monitoring, renewal reminders, and legal assistance included",
                color: "from-green-500 to-green-600"
              }
            ].map((benefit, idx) => (
              <div key={idx} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent rounded-3xl blur-2xl group-hover:blur-3xl transition-all" />
                <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border-2 border-white/20/50 rounded-3xl p-10 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300 hover:scale-105 h-full">
                  <div className="text-8xl font-black text-slate-800/50 absolute top-4 right-6">{benefit.number}</div>
                  <div className={`relative bg-gradient-to-br ${benefit.color} w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-2xl`}>
                    <benefit.icon className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-white font-black text-2xl mb-4">{benefit.title}</h3>
                  <p className="text-gray-400 text-lg leading-relaxed">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Mobile Slider */}
          <div className="md:hidden overflow-x-auto scrollbar-hide">
            <div className="flex gap-6 pb-4" style={{ scrollSnapType: 'x mandatory' }}>
              {[
                {
                  number: "01",
                  icon: Target,
                  title: "98% Success Rate",
                  desc: "Industry-leading approval rate backed by experienced trademark attorneys",
                  color: "from-blue-500 to-blue-600"
                },
                {
                  number: "02",
                  icon: Zap,
                  title: "24-48 Hour Filing",
                  desc: "Lightning-fast processing without compromising quality or accuracy",
                  color: "from-amber-500 to-amber-600"
                },
                {
                  number: "03",
                  icon: Shield,
                  title: "Lifetime Support",
                  desc: "Ongoing monitoring, renewal reminders, and legal assistance included",
                  color: "from-green-500 to-green-600"
                }
              ].map((benefit, idx) => (
                <div key={idx} className="flex-shrink-0 w-[280px] sm:w-[320px]" style={{ scrollSnapAlign: 'center' }}>
                  <div className="group relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent rounded-3xl blur-2xl" />
                    <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border-2 border-white/20/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 h-full">
                      <div className="text-5xl sm:text-6xl font-black text-slate-800/50 absolute top-3 sm:top-4 right-4 sm:right-6">{benefit.number}</div>
                      <div className={`relative bg-gradient-to-br ${benefit.color} w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-2xl`}>
                        <benefit.icon className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                      </div>
                      <h3 className="text-white font-black text-lg sm:text-xl mb-3 sm:mb-4">{benefit.title}</h3>
                      <p className="text-gray-400 text-sm sm:text-base leading-relaxed">{benefit.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Emphasized */}
      <section className="py-12 sm:py-16 lg:py-24 relative bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <div className="inline-block mb-3 sm:mb-4">
              <span className="text-amber-500 font-bold uppercase tracking-wider text-xs sm:text-sm">Limited Time Offer</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 sm:mb-6">
              Choose Your Protection Plan
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto px-4">
              Transparent pricing with no hidden fees. All packages include expert review.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {packages.map((pkg) => (
              <Card key={pkg.name} className={`bg-slate-900/60 border-white/20 relative ${pkg.popular ? 'border-2 border-white/60 shadow-2xl shadow-amber-500/20 scale-105' : 'hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02]'} transition-all duration-300 flex flex-col h-[600px]`}>
                {pkg.recommended && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs sm:text-sm font-black px-4 sm:px-6 py-2 rounded-full shadow-lg flex items-center gap-2">
                      <Star className="h-4 w-4 fill-white" />
                      POPULAR
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-4 flex-shrink-0">
                  <CardTitle className="text-white text-2xl font-bold mb-2">{pkg.name}</CardTitle>
                  <div className="text-5xl font-black text-amber-500 mb-2">{pkg.price}</div>
                  <div className="text-xs text-gray-500 mb-2">{pkg.note}</div>
                  <CardDescription className="text-gray-400 text-sm leading-relaxed">{pkg.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col flex-grow">
                  <div className="flex-grow overflow-y-auto mb-4 pr-2" style={{ maxHeight: '250px' }}>
                    <ul className="space-y-2">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-gray-300 text-sm">
                          <CheckCircle className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex-shrink-0">
                    <p className="text-xs text-gray-500 text-center mb-4">{pkg.disclaimer}</p>
                    <Button 
                      onClick={() => setIsPopupOpen(true)}
                      className={`w-full font-bold text-base ${pkg.popular ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/30' : 'bg-slate-800 hover:bg-slate-700'} text-white`}
                    >
                      Register My Trademark
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Auto-slide (Responsive: 1 on mobile, 2 on tablet, 3 on desktop) */}
      <section className="py-12 md:py-20 bg-slate-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4">
              What Our Clients Say
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              Join thousands of satisfied clients who trust us with their intellectual property.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative px-0 md:px-16 lg:px-20">
            <div className="overflow-hidden -mx-4 sm:-mx-6 lg:-mx-8">
              {/* Testimonial Cards Container - Responsive */}
              <div 
                className="flex transition-transform duration-700 ease-in-out"
                style={{ 
                  transform: `translateX(-${currentTestimonial * (100 / itemsPerView)}%)`
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="min-w-full md:min-w-[50%] lg:min-w-[33.333%] w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-4 sm:px-6 lg:px-8">
                    <Card className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border border-white/20 rounded-2xl p-4 md:p-6 h-full hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all">
                      <CardContent className="pt-4 md:pt-6">
                        <div className="flex items-center justify-center mb-4 md:mb-6">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="h-4 w-4 md:h-5 md:w-5 lg:h-6 lg:w-6 text-amber-500 fill-amber-500 mx-0.5" />
                          ))}
                        </div>
                        <p className="text-gray-300 text-sm sm:text-base md:text-lg text-center mb-4 md:mb-6 italic leading-relaxed">
                          "{testimonial.content}"
                        </p>
                        <div className="text-center border-t border-white/20 pt-3 md:pt-4">
                          <p className="text-white font-bold text-sm md:text-base">{testimonial.name}</p>
                          <p className="text-gray-400 text-xs md:text-sm mt-1">{testimonial.role}, {testimonial.company}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows - Hidden on mobile */}
            <button
              onClick={() => setCurrentTestimonial((prev) => Math.max(prev - 1, 0))}
              className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white p-3 rounded-full shadow-xl transition-all hover:scale-110 z-10"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={() => setCurrentTestimonial((prev) => prev < maxTestimonialIndex ? prev + 1 : prev)}
              className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white p-3 rounded-full shadow-xl transition-all hover:scale-110 z-10"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            {/* Dots Navigation - Show dots for valid positions only */}
            <div className="flex justify-center gap-2 md:gap-3 mt-8">
              {Array.from({ length: maxTestimonialIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-2 md:h-3 rounded-full transition-all ${
                    index === currentTestimonial 
                      ? 'bg-amber-500 w-8 md:w-12' 
                      : 'bg-slate-700 w-2 md:w-3 hover:bg-slate-600'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Progress Bar */}
            <div className="mt-6 bg-slate-800 rounded-full h-1 overflow-hidden max-w-md mx-auto">
              <div 
                className="bg-gradient-to-r from-amber-500 to-amber-600 h-full transition-all duration-700 ease-in-out"
                style={{ width: `${((currentTestimonial + 1) / testimonials.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Matching Homepage Style */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Got Questions?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              Everything You Need
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">to Know</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Quick answers to common trademark registration questions.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {[
              { q: "How long does trademark registration take?", a: "The entire process typically takes 8-12 months from filing to registration. However, you receive a filing date immediately upon submission, which establishes your priority rights. We file your application within 24-48 hours." },
              { q: "What makes your service different?", a: "We combine expert legal guidance with affordable pricing and fast service. Our 98% approval rate, experienced attorneys, and comprehensive support throughout the entire process set us apart from DIY services and expensive law firms." },
              { q: "Can I trademark a name that's already in use?", a: "It depends. If someone is using a similar name in a different industry or geographic area, you may still be able to register it. Our comprehensive trademark search helps identify potential conflicts before filing, saving you time and money." },
              { q: "What if my application gets rejected?", a: "Our service includes handling USPTO office actions. If the USPTO raises concerns, our experienced attorneys will work with you to address them. Our 98% approval rate reflects our expertise in overcoming these challenges." },
              { q: "Do I really need a trademark attorney?", a: "While not legally required, professional help dramatically increases your success rate. DIY applications have a 60% rejection rate, while our attorney-prepared applications have a 98% approval rate. We ensure everything is done correctly the first time." },
              { q: "What happens after my trademark is registered?", a: "After registration, we provide ongoing monitoring services, renewal reminders, and legal assistance to help you enforce your trademark rights. Your trademark registration lasts 10 years and can be renewed indefinitely." }
            ].map((faq, index) => (
              <Accordion key={index} type="single" collapsible>
                <AccordionItem value={`item-${index}`} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-2 hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all group">
                  <AccordionTrigger className="text-white font-bold text-left hover:text-amber-400 py-6 group-hover:scale-[1.01] transition-all">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <span className="text-amber-400 font-black text-lg">{index + 1}</span>
                      </div>
                      <span className="flex-1">{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pl-14 pr-4 pb-6 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>

          {/* CTA below FAQ */}
          <div className="mt-12 text-center">
            <p className="text-gray-300 text-lg mb-6">Still have questions? Our experts are here to help!</p>
            <Button 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-xl hover:shadow-amber-500/30 hover:scale-105 transition-all px-8"
            >
              Schedule Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA Banner - Eye-catching */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 via-amber-600/10 to-amber-500/20" />
        <div className="absolute inset-0 backdrop-blur-sm" />
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border-2 border-white/60 rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl shadow-amber-500/20">
            <div className="inline-flex items-center bg-green-500/20 border border-green-500/40 rounded-full px-4 sm:px-6 py-2 mb-4 sm:mb-6">
              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 mr-2" />
              <span className="text-green-400 font-bold text-xs sm:text-sm">⚡ Limited Spots Available This Month!</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 sm:mb-6 leading-tight">
              Don't Let Someone Steal
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Your Brand Name</span>
            </h2>
            
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Every day you wait, you risk losing your brand name to competitors. Join 5000+ smart business owners who secured their trademarks with us and sleep peacefully at night.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-base sm:text-lg md:text-xl font-black shadow-2xl hover:shadow-amber-500/50 hover:scale-110 transition-all px-6 sm:px-8 md:px-10 py-4 sm:py-5 md:py-7 w-full sm:w-auto"
              >
                Protect My Brand Now
                <ArrowRight className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => setIsPopupOpen(true)}
                className="border-2 border-white/60 text-white hover:bg-white/10 text-base sm:text-lg md:text-xl font-semibold px-6 sm:px-8 md:px-10 py-4 sm:py-5 md:py-7 w-full sm:w-auto"
              >
                Speak to an Attorney
              </Button>
            </div>
            
            <div className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Risk-Free Process</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Money-Back Guarantee</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Fast 24-48h Filing</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LandingPageFooter />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default LandingPage;
